/**
 * Patch 35.3b — Batch preflight modal.
 *
 * Runs one pure-read preflight against ``/media-engine/packs/{pack}/
 * batch-render/preflight`` and displays eligibility per asset. Only
 * founders / admins see projected spend. Confirms + calls execute
 * once; the modal blocks re-submission until the request completes.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/shared/ui/dialog";
import {
  batchRenderImageExecute,
  batchRenderImagePreflight,
  type BatchExecuteOut,
} from "@/entities/ai-studio";
import { safeBlockerCopy, useBatchSelection } from "@/entities/ai-studio/BatchSelectionContext";
import { toast } from "sonner";

interface Props {
  workspaceId: string;
  packId: string;
  assetIds: string[];
  isAdmin: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkingChange: (busy: boolean) => void;
}

export function BatchPreflightModal({
  workspaceId, packId, assetIds, isAdmin, onOpenChange, onWorkingChange,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const { clear } = useBatchSelection();
  const [result, setResult] = useState<BatchExecuteOut | null>(null);

  const preflight = useQuery({
    queryKey: ["batch-preflight", workspaceId, packId, assetIds.join(",")],
    queryFn: () => batchRenderImagePreflight(workspaceId, packId, assetIds),
    enabled: !result,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const execute = useMutation({
    mutationFn: (ids: string[]) => batchRenderImageExecute(workspaceId, packId, ids),
    onMutate: () => onWorkingChange(true),
    onSuccess: (data) => {
      setResult(data);
      const completed = data.jobs.filter((j) => j.state === "completed").length;
      const failed = data.jobs.filter((j) => j.state === "failed").length;
      if (completed > 0) {
        toast.success(
          completed === 1
            ? "1 image ready for review"
            : `${completed} images ready for review`,
        );
      }
      if (failed > 0) {
        toast.error(
          failed === 1
            ? "1 image couldn't be created"
            : `${failed} images couldn't be created`,
        );
      }
      // Live invalidation across every canonical query touched by
      // this batch. Selection is cleared unconditionally so the bar
      // dismisses even if no siblings completed.
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets", workspaceId, packId] });
      void qc.invalidateQueries({ queryKey: ["pack-summary", workspaceId, packId] });
      void qc.invalidateQueries({ queryKey: ["ops-packs", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["asset-lifecycle", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["ops-versions"] });
      clear();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Couldn't render this batch. Please try again.");
    },
    onSettled: () => onWorkingChange(false),
  });

  const canRun = preflight.data && preflight.data.eligible_count > 0 && !execute.isPending && !result;
  const eligibleIds = (preflight.data?.assets || [])
    .filter((a) => a.eligible).map((a) => a.asset_id);

  const submit = () => {
    if (!canRun || eligibleIds.length === 0) return;
    execute.mutate(eligibleIds);
  };

  // Ensure the working flag resets when modal closes cleanly.
  useEffect(() => () => onWorkingChange(false), [onWorkingChange]);

  return (
    <Dialog open onOpenChange={(v) => !execute.isPending && onOpenChange(v)}>
      <DialogContent data-testid="batch-preflight-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {result
              ? "Batch complete"
              : `Create ${assetIds.length} campaign ${assetIds.length === 1 ? "image" : "images"}?`}
          </DialogTitle>
          <DialogDescription>
            {result
              ? "See per-item results below. Rendered drafts are back in Review."
              : "Orkexra will render these approved prompts and return the finished images to Review. You will approve each final image before it enters the Vault. Nothing will be scheduled or published automatically."}
          </DialogDescription>
        </DialogHeader>

        {!result && preflight.isLoading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking eligibility…
          </div>
        )}

        {!result && preflight.data && (
          <div className="space-y-3">
            <ul
              data-testid="batch-preflight-list"
              className="max-h-64 space-y-1.5 overflow-y-auto rounded-md border border-border/60 p-3 text-xs"
            >
              {preflight.data.assets.map((a) => (
                <li
                  key={a.asset_id}
                  data-testid={`batch-preflight-row-${a.asset_id}`}
                  className="flex items-start gap-2"
                >
                  {a.eligible ? (
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                  )}
                  <div className="flex-1">
                    <div className="font-mono text-[11px]">
                      {a.content_type || a.asset_id}
                    </div>
                    {!a.eligible && (
                      <div className="text-muted-foreground">
                        {safeBlockerCopy(a.blocker) || a.blocker_detail || "Not eligible."}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="text-xs text-muted-foreground">
              {preflight.data.eligible_count} of {preflight.data.requested_count} eligible
              · max {preflight.data.max_batch_size} per batch
            </div>

            {isAdmin && preflight.data.projected_cost_usd != null && (
              <div
                data-testid="batch-preflight-cost"
                className="rounded-md bg-secondary/40 p-2 text-xs text-muted-foreground"
              >
                Estimated production use: approximately $
                {(preflight.data.projected_cost_usd || 0).toFixed(2)}
              </div>
            )}
            {!isAdmin && (
              <div
                data-testid="batch-preflight-standard-note"
                className="rounded-md bg-secondary/40 p-2 text-xs text-muted-foreground"
              >
                This action uses AI production capacity.
              </div>
            )}
            {preflight.data.eligible_count === 0 && (
              <div className="rounded-md border border-amber-300/50 bg-amber-500/5 p-2 text-xs text-amber-700">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                No items are eligible right now. Close and update the affected prompts, then try again.
              </div>
            )}
          </div>
        )}

        {result && (
          <ul
            data-testid="batch-result-list"
            className="max-h-72 space-y-1.5 overflow-y-auto rounded-md border border-border/60 p-3 text-xs"
          >
            {result.jobs.map((j) => (
              <li
                key={j.asset_id}
                data-testid={`batch-result-row-${j.asset_id}-${j.state}`}
                className="flex items-start gap-2"
              >
                {j.state === "completed" && (
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                )}
                {(j.state === "failed" || j.state === "already_running" || j.state === "blocked") && (
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                )}
                <div className="flex-1">
                  <div className="font-mono text-[11px]">
                    {j.asset_id.slice(0, 8)}…
                  </div>
                  <div className="text-muted-foreground">
                    {j.state === "completed" && "Ready for review"}
                    {j.state === "failed" && "Couldn't create image"}
                    {j.state === "already_running" && "Already rendering"}
                    {j.state === "blocked" &&
                      (safeBlockerCopy(j.blocker) || "This item can't be rendered right now.")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="gap-2">
          {result ? (
            <Button
              data-testid="batch-close"
              onClick={() => onOpenChange(false)}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                data-testid="batch-cancel"
                onClick={() => onOpenChange(false)}
                disabled={execute.isPending}
              >
                Cancel
              </Button>
              <Button
                data-testid="batch-confirm-create"
                onClick={submit}
                disabled={!canRun || execute.isPending}
              >
                {execute.isPending ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create Images"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
